import { AlertCircle, CheckCircle2, Download, FileUp } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

export default function ApplicationUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{
    successCount: number;
    errorCount: number;
    errorDetails: string[];
  } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/applications/upload', {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        const data = (await res.json()) as {
          successCount: number;
          errorCount: number;
          errorDetails: string[];
        };
        setResult(data);
      }
    } catch (error) {
      console.error('Upload failed', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">追加情報一括紐付け</h1>
        <p className="text-gray-500 mt-1">
          調査書受理、追加課題提出、または外部試験スコア等のデータをCSV/Excelで一括紐付けします。
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>アップロード設定</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4 p-4 border-2 border-dashed rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="p-3 bg-blue-100 rounded-full text-blue-600">
                  <FileUp className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-900 cursor-pointer">
                    ファイルをドラッグ＆ドロップ、または選択{' '}
                    <input type="file" className="hidden" onChange={handleFileChange} accept=".csv,.xlsx" />
                  </label>
                  <p className="text-xs text-gray-500 mt-1">対応形式: CSV, Excel (最大 10MB)</p>
                </div>
                {file && (
                  <Badge variant="secondary" className="px-3 py-1">
                    {file.name}
                  </Badge>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-bold mb-1">照合キーの確認</p>
                  <p>
                    ファイルの1列目は「受付番号 (Receipt Number)」または「管理用申込ID (Application
                    ID)」である必要があります。
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {result && (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2 text-emerald-700 font-bold">
                  <CheckCircle2 className="w-5 h-5" />
                  処理が完了しました
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-white p-3 rounded border border-emerald-100 shadow-sm">
                    <div className="text-gray-500">正常処理件数</div>
                    <div className="text-2xl font-bold text-emerald-600">{result.successCount} 件</div>
                  </div>
                  <div className="bg-white p-3 rounded border border-emerald-100 shadow-sm">
                    <div className="text-gray-500">エラー件数</div>
                    <div className="text-2xl font-bold text-red-600">{result.errorCount} 件</div>
                  </div>
                </div>
                {result.errorDetails && result.errorDetails.length > 0 && (
                  <div className="text-xs text-red-600 font-mono max-h-40 overflow-auto bg-gray-900 p-2 rounded">
                    {result.errorDetails.map((err, i) => (
                      <div key={`${i}-${err.substring(0, 20)}`}>{err}</div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-bold">テンプレート</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" className="w-full justify-start text-xs h-auto py-3">
                <Download className="w-4 h-4 mr-2" /> 調査書受理管理用テンプレート
              </Button>
              <Button variant="outline" className="w-full justify-start text-xs h-auto py-3">
                <Download className="w-4 h-4 mr-2" /> 追加設問回答一括登録テンプレート
              </Button>
              <Separator />
              <div className="text-[10px] text-gray-500">
                テンプレートには現在の募集期間中の有効な申込データ（受付番号・氏名）が含まれます。
              </div>
            </CardContent>
          </Card>

          <Button
            className="w-full bg-blue-600 hover:bg-blue-700 h-12 shadow-lg"
            disabled={!file || uploading}
            onClick={handleUpload}
          >
            {uploading ? '処理中...' : 'アップロード開始'}
          </Button>
        </div>
      </div>
    </div>
  );
}
